<script setup lang="ts">
import type { WoningtypeStatsResponse } from '~/types'

const props = defineProps<{
  type: string
  label: string
}>()

const content = {
  price: 'Vraagprijs',
  area: 'Woonoppervlakte',
  pricePerM2: 'Vraagprijs per m²',
  days: 'Te koop',
  daysUnit: 'dagen',
  vsAll: 'alle woningen',
  citiesTitle: 'Hier werden de meeste verkocht',
  error: 'De verkoopcijfers zijn tijdelijk niet beschikbaar.',
}

const MIN_SALES = 20
const MIN_SALES_FOR_CITIES = 100

const { data, status } = await useAsyncData(`woningtype-${props.type}`, () =>
  $fetch<WoningtypeStatsResponse>(`/api/data/woningtype/${props.type}`),
)

const updated = computed(() => data.value?.type.bijgewerkt_op
  ? new Date(data.value.type.bijgewerkt_op).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
  : '')

const diff = (value: number, base: number) => {
  if (!base) return ''
  const pct = Math.round((value / base - 1) * 100)
  if (pct === 0) return `gelijk aan ${content.vsAll}`
  return `${pct > 0 ? '+' : ''}${pct}% t.o.v. ${content.vsAll}`
}

const stats = computed(() => {
  if (!data.value) return []
  const { type, alle } = data.value
  return [
    { label: content.price, value: euro(type.mediaan_prijs), note: diff(type.mediaan_prijs, alle.mediaan_prijs) },
    { label: content.area, value: `${formatNumber(type.mediaan_m2)} m²`, note: `${content.vsAll}: ${formatNumber(alle.mediaan_m2)} m²` },
    { label: content.pricePerM2, value: euro(type.mediaan_prijs_m2), note: diff(type.mediaan_prijs_m2, alle.mediaan_prijs_m2) },
    { label: content.days, value: `${type.mediaan_dagen} ${content.daysUnit}`, note: `${content.vsAll}: ${alle.mediaan_dagen} ${content.daysUnit}` },
  ]
})

const source = computed(() => data.value
  ? `Gebaseerd op ${formatNumber(data.value.type.verkocht)} ${props.label} die in de afgelopen 12 maanden zijn verkocht, volgens de Funda-gegevens die JuisteMakelaar analyseert. Alle cijfers zijn medianen. Bijgewerkt op ${updated.value}.`
  : '')
</script>

<template>
  <div
    v-if="status === 'success' && data && data.type.verkocht >= MIN_SALES"
    class="not-prose my-6 rounded-xl border border-default bg-default p-5 sm:p-6"
  >
    <dl class="grid grid-cols-2 lg:grid-cols-4 gap-5">
      <div
        v-for="stat in stats"
        :key="stat.label"
      >
        <dt class="text-sm text-muted">
          {{ stat.label }}
        </dt>
        <dd class="text-2xl font-bold text-highlighted tabular-nums">
          {{ stat.value }}
        </dd>
        <dd class="text-xs text-dimmed">
          {{ stat.note }}
        </dd>
      </div>
    </dl>

    <div
      v-if="data.type.verkocht >= MIN_SALES_FOR_CITIES && data.type.top_plaatsen.length"
      class="mt-5 border-t border-default pt-4"
    >
      <p class="text-sm font-medium text-highlighted mb-2">
        {{ content.citiesTitle }}
      </p>
      <ol class="flex flex-wrap gap-2">
        <li
          v-for="city in data.type.top_plaatsen"
          :key="city.plaats"
        >
          <UBadge
            color="neutral"
            variant="subtle"
            :label="`${city.plaats} (${formatNumber(city.aantal)})`"
          />
        </li>
      </ol>
    </div>

    <p class="mt-4 text-xs text-dimmed text-pretty">
      {{ source }}
    </p>
  </div>
  <p
    v-else-if="status === 'error'"
    class="text-sm text-muted"
  >
    {{ content.error }}
  </p>
</template>
